import React, { useState, useEffect } from 'react';
import { Eye } from 'lucide-react';
import { API_URL } from '@/api/constant';
import { StatusBadge } from './StatusBadge';
import ViewDocumentModal, { DocumentItem } from './ViewDocumentModal';

interface MediaItem {
    url: string;
    [key: string]: any;
}

interface DetailsGalleryProps {
    media: MediaItem[];
    name: string;
    status?: string;
    className?: string;
}

export const DetailsGallery: React.FC<DetailsGalleryProps> = ({
    media,
    name,
    status,
    className = ''
}) => {
    const [activeIndex, setActiveIndex] = useState(0);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalIndex, setModalIndex] = useState(0);

    useEffect(() => {
        if (media && media.length > 0) {
            setActiveIndex(0);
        }
    }, [media]);

    if (!media || media.length === 0) {
        return (
            <div className={`bg-card rounded-2xl border shadow-sm p-4 ${className}`}>
                <div className="aspect-square rounded-xl overflow-hidden bg-secondary/10 border border-secondary/20 flex items-center justify-center text-muted-foreground">
                    No Image Available
                </div>
            </div>
        );
    }

    // Build DocumentItem list for the modal
    const docItems: DocumentItem[] = media.map((img, i) => ({
        id: img.id ?? String(i),
        url: `${API_URL}/${img.url}`,
        label: `${name} — Image ${i + 1}`,
        subtitle: img.referenceType ?? img.type ?? undefined,
        uploadedAt: img.createdAt ?? undefined,
    }));

    const activeUrl = docItems[activeIndex]?.url ?? '';

    const openModal = (idx: number) => {
        setModalIndex(idx);
        setModalOpen(true);
    };

    console.log(docItems, "ssss");

    return (
        <div className={`bg-card rounded-2xl border shadow-sm p-4 ${className}`}>
            {/* ── Main Image ── */}
            <div
                className="aspect-square rounded-xl overflow-hidden bg-secondary/10 border border-secondary/20 mb-4 group relative cursor-zoom-in"
                onClick={() => openModal(activeIndex)}
                title="Click to view full image"
            >
                <img
                    src={activeUrl}
                    alt={name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    onError={(e) => {
                        (e.target as HTMLImageElement).src = 'https://placehold.co/600x600?text=No+Image';
                    }}
                />

                {/* Hover overlay — eye icon */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-colors flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 rounded-full p-3 shadow-lg">
                        <Eye className="w-6 h-6 text-primary cursor-pointer" />
                    </div>
                </div>

                {/* Status badge */}
                {status && (
                    <div className="absolute top-3 right-3">
                        <StatusBadge status={status} />
                    </div>
                )}
            </div>

            {/* ── Thumbnails ── */}
            {media.length > 1 && (
                <div className="grid grid-cols-4 gap-3">
                    {docItems.map((doc, i) => (
                        <button
                            key={doc.id}
                            onClick={() => setActiveIndex(i)}
                            onDoubleClick={() => openModal(i)}
                            className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${activeIndex === i
                                ? 'border-primary shadow-md'
                                : 'border-transparent hover:border-secondary-foreground/20 opacity-70 hover:opacity-100'
                                }`}
                            title="Click to select · Double-click to view full"
                        >
                            <img
                                src={doc.url}
                                alt={doc.label}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                    (e.target as HTMLImageElement).src = 'https://placehold.co/100x100?text=Error';
                                }}
                            />
                        </button>
                    ))}
                </div>
            )}

            {/* ── View Document Modal ── */}
            <ViewDocumentModal
                open={modalOpen}
                onOpenChange={setModalOpen}
                documents={docItems}
                initialIndex={modalIndex}
            />
        </div>
    );
};
